import React, { Component } from 'react';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/700.css';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/500.css';
import '@fontsource/dm-sans/400.css';
import "@/App.css";
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import TablesPage from "./pages/TablesPage";
import MenuPage from "./pages/MenuPage";
import OrdersPage from "./pages/OrdersPage";
import CheckoutPage from "./pages/CheckoutPage";
import PastOrdersPage from "./pages/PastOrdersPage";
import RevenuePage from "./pages/RevenuePage";
import EmployeesPage from "./pages/EmployeesPage";
import SettingsPage from "./pages/SettingsPage";
import ReservationsPage from "./pages/ReservationsPage";
import CustomerMenuPage from "./pages/CustomerMenuPage";
import LandingPage from "./pages/LandingPage";
import LegalPage from "./pages/LegalPage";
import InventoryPage from "./pages/InventoryPage";
import { Toaster } from "./components/ui/sonner";
import { jwtDecode } from "jwt-decode";

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  try {
    const decoded = jwtDecode(token);
    // M-3 fix: check token expiry client-side too
    if (decoded.exp && decoded.exp < Date.now() / 1000) {
      localStorage.removeItem('admin_token');
      sessionStorage.removeItem('admin_token');
      return <Navigate to="/login" replace />;
    }
  } catch (e) {
    localStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_token');
    return <Navigate to="/login" replace />;
  }
  return children;
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo: error.toString() });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', background: '#0f172a', color: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Bir hata oluştu</h2>
          <p style={{ color: '#94a3b8' }}>Lütfen uygulamayı yeniden başlatın. Sorun devam ederse destek ekibi ile iletişime geçin.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const isElectron = navigator.userAgent.toLowerCase().includes('electron');

  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // URL parametresi ile gelen QR kodlarını güvenli şekilde HashRouter'a yönlendir
  const searchParams = new URLSearchParams(window.location.search);
  const qrParam = searchParams.get('qr');

  // Fix for Radix UI stuck pointer-events bug
  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style') {
          if (document.body.style.pointerEvents === 'none') {
            const hasOpenDialog = document.querySelector('[role="dialog"]') || document.querySelector('[data-radix-popper-content-wrapper]');
            if (!hasOpenDialog) {
              document.body.style.pointerEvents = 'auto';
            }
          }
        }
      });
    });
    observer.observe(document.body, { attributes: true });
    
    // Fallback cleanup on interval just in case
    const interval = setInterval(() => {
      const hasOpenDialog = document.querySelector('[role="dialog"]') || document.querySelector('[data-radix-popper-content-wrapper]');
      if (!hasOpenDialog && document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = 'auto';
      }
    }, 2000);
    
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  return (
    <ErrorBoundary>
      <div className="App bg-slate-50 dark:bg-slate-950 min-h-screen text-slate-900 dark:text-slate-100">
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
            <Route path="/admin/tables" element={<PrivateRoute><TablesPage /></PrivateRoute>} />
            <Route path="/admin/reservations" element={<PrivateRoute><ReservationsPage /></PrivateRoute>} />
            <Route path="/admin/menu" element={<PrivateRoute><MenuPage /></PrivateRoute>} />
            <Route path="/admin/orders" element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
            <Route path="/admin/checkout/:tableNumber" element={<PrivateRoute><CheckoutPage /></PrivateRoute>} />
            <Route path="/admin/past-orders" element={<PrivateRoute><PastOrdersPage /></PrivateRoute>} />
            <Route path="/admin/revenue" element={<PrivateRoute><RevenuePage /></PrivateRoute>} />
            <Route path="/admin/employees" element={<PrivateRoute><EmployeesPage /></PrivateRoute>} />
            <Route path="/admin/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
            <Route path="/admin/inventory" element={<PrivateRoute><InventoryPage /></PrivateRoute>} />
            <Route path="/menu/:qrCode" element={<CustomerMenuPage />} />
            <Route path="/sozlesme/:docId" element={<LegalPage />} />
            <Route path="/" element={
              qrParam ? <Navigate to={`/menu/${qrParam}`} replace /> 
              : (isElectron ? <Navigate to="/login" replace /> : <LandingPage />)
            } />
          </Routes>
        </Router>
        <Toaster position="top-right" />
      </div>
    </ErrorBoundary>
  );
}

export default App;